import { DateTime } from 'luxon';
import { ReactElement, useMemo } from 'react';
import { QueryActivityPageHour } from '../../../api/schema';
import ActivityBarChart, { ActivityBar } from './ActivityBarChart';
import { BOOMERANG_SERIES, EQUIPMENT_COUNT_SERIES } from './activityColors';
import { ActivityGrain } from './activityWindow';

export type HourlyView = 'clock' | 'day';

type HourlyMovementChartProps = {
  hourly: QueryActivityPageHour[];
  grain: ActivityGrain;
  view: HourlyView;
  tz: string;
  fetching: boolean;
  /** Draw the two boomerang classes instead of the two directions. */
  boomerangs?: boolean;
  height?: number;
  /** Opens one day. Only offered when the bars are days: an hour-of-day
   *  histogram is an aggregate with no single period behind it. */
  onSelectDay?: (date: string) => void;
};

type Bucket = {
  key: string;
  label: string;
  title: string;
  outReal: number;
  inReal: number;
  outBoomerangReal: number;
  outBoomerangArtifact: number;
  sites: number;
};

/**
 * When equipment leaves and when it comes back.
 *
 * A single day is drawn hour by hour, which is the question a supervisor
 * actually has about a day ("did the trucks get out in the morning?"). A week
 * or a month is drawn by day instead, or as a clock -- 168 to 744 hourly bars
 * are unreadable, and both views come out of the same response.
 */
export const HourlyMovementChart = ({
  hourly,
  grain,
  view,
  tz,
  fetching,
  boomerangs = false,
  height = 240,
  onSelectDay,
}: HourlyMovementChartProps): ReactElement => {
  // Hour-of-clock buckets when the window is a single day, or when the reader
  // asked for the by-hour view of a longer one.
  const byClock = grain === 'day' || view === 'clock';

  const bars = useMemo<ActivityBar[]>(() => {
    const buckets = new Map<string, Bucket>();

    hourly.forEach((row) => {
      const at = DateTime.fromISO(row.hour || '', { zone: tz });
      if (!at.isValid) return;
      const key = byClock ? at.toFormat('HH') : at.toISODate() || '';
      if (!buckets.has(key)) {
        buckets.set(key, {
          key,
          label: byClock ? at.toFormat('HH') : at.toFormat('MMM d'),
          title: byClock ? `${at.toFormat('HH:00')} – ${at.plus({ hours: 1 }).toFormat('HH:00')}` : at.toFormat('EEE, MMM d'),
          outReal: 0,
          inReal: 0,
          outBoomerangReal: 0,
          outBoomerangArtifact: 0,
          sites: 0,
        });
      }
      const bucket = buckets.get(key) as Bucket;
      if (row.direction === 'out') {
        bucket.outReal += row.real || 0;
        // Boomerangs are counted on the way out, so the bar sits at the hour
        // the equipment left rather than being counted twice.
        bucket.outBoomerangReal += row.boomerang_real || 0;
        bucket.outBoomerangArtifact += row.boomerang_artifact || 0;
      } else {
        bucket.inReal += row.real || 0;
      }
      bucket.sites = Math.max(bucket.sites, row.sites || 0);
    });

    const ordered = Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
    // Hour-of-day labels get crowded; every third one is enough to orient.
    return ordered.map((bucket) => ({
      key: bucket.key,
      label: byClock && Number(bucket.key) % 3 ? '' : bucket.label,
      title: bucket.title,
      values: boomerangs ? [bucket.outBoomerangReal, bucket.outBoomerangArtifact] : [bucket.outReal, bucket.inReal],
      notes: boomerangs
        ? undefined
        : [
            { name: 'Sites', value: String(bucket.sites) },
            { name: 'Returns / Cancellations', value: `${bucket.outBoomerangReal} / ${bucket.outBoomerangArtifact}` },
          ],
    }));
  }, [hourly, byClock, tz, boomerangs]);

  return (
    <ActivityBarChart
      bars={bars}
      series={boomerangs ? BOOMERANG_SERIES : EQUIPMENT_COUNT_SERIES}
      fetching={fetching}
      height={height}
      emptyText={boomerangs ? 'No round trips in this period' : 'Nothing moved in this period'}
      // A day bucket opens that day. An hour-of-day bucket is an aggregate
      // across the whole period, so there is no single day to open.
      onSelect={onSelectDay && !byClock ? (index) => onSelectDay(bars[index].key) : undefined}
    />
  );
};

export default HourlyMovementChart;
